import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Upload,
    Image as ImageIcon,
    Video,
    Trash2,
    LogOut,
    ArrowLeft,
    Save,
    EyeOff,
    Eye,
} from "lucide-react";
import { api, ASSETS, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SECTIONS = [
    { value: "gallery", label: "Gallery" },
    { value: "featured", label: "Featured Performance" },
    { value: "founder", label: "Founder" },
    { value: "courses", label: "Courses" },
    { value: "testimonials", label: "Testimonials" },
    { value: "hero", label: "Hero" },
    { value: "anthem", label: "Anthem" },
    { value: "vision", label: "Vision" },
    { value: "social", label: "Social Service" },
    { value: "other", label: "Other" },
];

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

function MediaCard({ m, onToggle, onDelete, onSave }) {
    const [title, setTitle] = useState(m.title || "");
    const [caption, setCaption] = useState(m.caption || "");

    return (
        <div
            data-testid={`admin-media-${m.media_id}`}
            className="bg-white border border-gold-100"
        >
            {m.kind === "photo" ? (
                <img
                    src={m.data_url}
                    alt={m.title || m.caption || "photo"}
                    className="w-full h-44 object-cover"
                />
            ) : (
                <div className="w-full h-44 bg-maroon text-ivory flex items-center justify-center">
                    <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-display italic text-lg underline"
                    >
                        ▶ Watch video
                    </a>
                </div>
            )}
            <div className="p-4">
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => title !== (m.title || "") && onSave(m, { title })}
                    placeholder="Title"
                    data-testid={`admin-title-${m.media_id}`}
                    className="w-full font-display text-lg text-maroon border-b border-gold-100 bg-transparent pb-1"
                />
                <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    onBlur={() => caption !== (m.caption || "") && onSave(m, { caption })}
                    rows={2}
                    placeholder="Caption"
                    className="mt-2 w-full font-serif text-sm text-ink/80 bg-cream border border-gold-100 px-2 py-1"
                />
                <div className="mt-3 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase">
                    <button
                        onClick={() => onToggle(m)}
                        data-testid={`admin-toggle-${m.media_id}`}
                        className={
                            "inline-flex items-center gap-1 " +
                            (m.published ? "text-maroon" : "text-inkmuted")
                        }
                    >
                        {m.published ? (
                            <>
                                <Eye size={12} /> Published
                            </>
                        ) : (
                            <>
                                <EyeOff size={12} /> Hidden
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => onDelete(m)}
                        data-testid={`admin-delete-${m.media_id}`}
                        className="inline-flex items-center gap-1 text-maroon hover:text-maroon-700"
                    >
                        <Trash2 size={12} /> Delete
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Admin() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    // form
    const [kind, setKind] = useState("photo");
    const [section, setSection] = useState("gallery");
    const [title, setTitle] = useState("");
    const [caption, setCaption] = useState("");
    const [dataUrl, setDataUrl] = useState("");
    const [previewName, setPreviewName] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isAdmin = user && typeof user === "object" && user.role === "admin";

    useEffect(() => {
        if (user === false) {
            navigate("/login?next=/admin", { replace: true });
        }
    }, [user, navigate]);

    useEffect(() => {
        if (!isAdmin) return;
        api.get("/admin/media")
            .then((r) => setItems(r.data || []))
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed"))
            .finally(() => setLoading(false));
    }, [isAdmin]);

    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 4 * 1024 * 1024) {
            toast.error("Image exceeds 4 MB. Please compress or resize.");
            return;
        }
        const url = await fileToDataUrl(f);
        setDataUrl(url);
        setPreviewName(f.name);
    };

    const reset = () => {
        setTitle("");
        setCaption("");
        setDataUrl("");
        setPreviewName("");
        setVideoUrl("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const body = { kind, section, title, caption };
            if (kind === "photo") body.data_url = dataUrl;
            else body.url = videoUrl;

            if (kind === "photo" && !dataUrl) {
                toast.error("Please select an image.");
                setSubmitting(false);
                return;
            }
            if (kind === "video" && !videoUrl) {
                toast.error("Please paste a video URL.");
                setSubmitting(false);
                return;
            }

            const { data } = await api.post("/admin/media", body);
            setItems((prev) => [data, ...prev]);
            reset();
            toast.success("Uploaded.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setSubmitting(false);
        }
    };

    const togglePublish = async (m) => {
        try {
            const { data } = await api.patch(`/admin/media/${m.media_id}`, {
                published: !m.published,
            });
            setItems((prev) => prev.map((x) => (x.media_id === m.media_id ? data : x)));
        } catch {
            toast.error("Could not update.");
        }
    };

    const saveEdit = async (m, patch) => {
        try {
            const { data } = await api.patch(`/admin/media/${m.media_id}`, patch);
            setItems((prev) => prev.map((x) => (x.media_id === m.media_id ? data : x)));
            toast.success("Saved.");
        } catch {
            toast.error("Save failed.");
        }
    };

    const remove = async (m) => {
        if (!window.confirm("Delete this media permanently?")) return;
        try {
            await api.delete(`/admin/media/${m.media_id}`);
            setItems((prev) => prev.filter((x) => x.media_id !== m.media_id));
            toast("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const bySection = useMemo(() => {
        const g = {};
        items.forEach((m) => {
            g[m.section] = g[m.section] || [];
            g[m.section].push(m);
        });
        return g;
    }, [items]);

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ivory eyebrow text-maroon">
                Loading…
            </div>
        );
    }

    if (user && !isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ivory">
                <div className="max-w-md text-center p-10 card-cream">
                    <div className="eyebrow text-gold">· Restricted ·</div>
                    <h1 className="font-display text-3xl text-maroon mt-3">
                        Admin access required.
                    </h1>
                    <p className="font-serif text-ink/80 mt-4">
                        Please sign in with an admin account to manage the Gurukula media.
                    </p>
                    <Link to="/" className="btn-primary mt-6 inline-block">
                        Back to Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="min-h-screen bg-ivory">
            <header className="border-b border-gold-100 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <img src={ASSETS.logo} alt="Logo" className="h-10 w-10 rounded-full ring-1 ring-gold/40" />
                        <div className="leading-tight">
                            <div className="font-display text-base text-maroon font-semibold">
                                Shri Vishwa Bandhu
                            </div>
                            <div className="eyebrow text-[10px] text-gold">Admin · Media Manager</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="text-[12px] tracking-[0.2em] uppercase text-ink hover:text-maroon inline-flex items-center gap-1"
                        >
                            <ArrowLeft size={14} /> Site
                        </Link>
                        <button
                            onClick={async () => {
                                await logout();
                                navigate("/", { replace: true });
                            }}
                            data-testid="admin-logout"
                            className="btn-ghost-gold inline-flex items-center gap-2"
                        >
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Upload panel */}
                <section className="lg:col-span-5">
                    <div className="eyebrow">· New upload ·</div>
                    <h1 className="font-display text-3xl text-maroon mt-3 leading-tight">
                        Add a photo or video.
                    </h1>

                    <form
                        onSubmit={submit}
                        data-testid="admin-upload-form"
                        className="mt-6 bg-white border border-gold-100 p-6 space-y-5"
                    >
                        <div className="flex gap-2">
                            {[
                                { v: "photo", Icon: ImageIcon, label: "Photo" },
                                { v: "video", Icon: Video, label: "Video URL" },
                            ].map(({ v, Icon, label }) => (
                                <button
                                    type="button"
                                    key={v}
                                    onClick={() => setKind(v)}
                                    data-testid={`admin-kind-${v}`}
                                    className={
                                        "flex-1 inline-flex items-center justify-center gap-2 text-[12px] tracking-[0.2em] uppercase px-4 py-2 border " +
                                        (kind === v
                                            ? "bg-maroon text-ivory border-maroon"
                                            : "text-ink border-gold-100 hover:border-maroon")
                                    }
                                >
                                    <Icon size={14} /> {label}
                                </button>
                            ))}
                        </div>

                        <div>
                            <label className="eyebrow text-ink/70">Section</label>
                            <select
                                value={section}
                                onChange={(e) => setSection(e.target.value)}
                                data-testid="admin-section-select"
                                className="mt-2 w-full bg-cream border border-gold-100 px-3 py-2 font-serif text-ink"
                            >
                                {SECTIONS.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="eyebrow text-ink/70">Title (optional)</label>
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                data-testid="admin-title-input"
                                className="mt-2 w-full bg-cream border border-gold-100 px-3 py-2 font-serif text-ink"
                                placeholder="e.g. Annual Day 2026"
                            />
                        </div>
                        <div>
                            <label className="eyebrow text-ink/70">Caption (optional)</label>
                            <textarea
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                data-testid="admin-caption-input"
                                rows={3}
                                className="mt-2 w-full bg-cream border border-gold-100 px-3 py-2 font-serif text-ink"
                                placeholder="A short description shown alongside the media"
                            />
                        </div>

                        {kind === "photo" ? (
                            <div>
                                <label className="eyebrow text-ink/70">
                                    Photo file (JPG / PNG · max 4 MB)
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFile}
                                    data-testid="admin-file-input"
                                    className="mt-2 block w-full text-sm text-ink"
                                />
                                {dataUrl && (
                                    <div className="mt-3 border border-gold-100 p-2">
                                        <img
                                            src={dataUrl}
                                            alt="preview"
                                            className="w-full h-40 object-cover"
                                        />
                                        <div className="eyebrow text-gold mt-2">{previewName}</div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <label className="eyebrow text-ink/70">
                                    Video URL (YouTube / Vimeo)
                                </label>
                                <input
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    data-testid="admin-video-input"
                                    className="mt-2 w-full bg-cream border border-gold-100 px-3 py-2 font-serif text-ink"
                                    placeholder="https://youtu.be/…"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-submit"
                            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            <Upload size={14} /> {submitting ? "Uploading…" : "Publish"}
                        </button>
                    </form>
                </section>

                {/* Library */}
                <section className="lg:col-span-7">
                    <div className="eyebrow">· Media Library · {items.length} items ·</div>
                    <h2 className="font-display text-3xl text-maroon mt-3 leading-tight">
                        All uploaded media.
                    </h2>

                    {loading ? (
                        <p className="mt-8 eyebrow text-gold">Loading…</p>
                    ) : items.length === 0 ? (
                        <p className="mt-8 font-serif text-ink/70">
                            Nothing uploaded yet — add your first photo or video from the left.
                        </p>
                    ) : (
                        <div className="mt-6 space-y-8">
                            {Object.entries(bySection).map(([sec, list]) => (
                                <div key={sec}>
                                    <div className="eyebrow text-gold mb-3">· {sec} ·</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {list.map((m) => (
                                            <MediaCard
                                                key={m.media_id}
                                                m={m}
                                                onToggle={togglePublish}
                                                onDelete={remove}
                                                onSave={saveEdit}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}
